import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import SEOHead from '@/components/feature/SEOHead';
import SchemaOrg, { getWebPageSchema, getBreadcrumbSchema, getFAQSchema, getOrganizationSchema } from '@/components/feature/SchemaOrg';
import Breadcrumb, { getBreadcrumbForAbout } from '@/components/feature/Breadcrumb';
import { siteInfo } from '@/mocks/siteInfo';

export default function AboutPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const pageTitle = '关于我们 - 开云体育';
  const pageDesc = '了解开云体育品牌故事、平台信誉、核心优势及联系方式。开云体育，您值得信赖的体育竞技平台。';
  const pageUrl = 'https://www.kaiyunsports.com/about';
  const breadcrumbItems = getBreadcrumbForAbout();
  const breadcrumbSchemaItems = breadcrumbItems.map((b) => ({
    name: b.label,
    url: b.href ? `https://www.kaiyunsports.com${b.href}` : undefined,
  }));

  const faqSchemaData = siteInfo.faqs.slice(0, 5).map((f) => ({
    question: f.question,
    answer: f.answer,
  }));

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus('idle');
    setFormError('');

    const form = e.currentTarget;
    const formData = new FormData(form);

    const honeypot = formData.get('phone_alt') as string;
    if (honeypot && honeypot.trim() !== '') {
      setFormStatus('success');
      return;
    }

    try {
      const response = await fetch('https://readdy.ai/api/form/d9rf9gme3oq4jqumu0jg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(
          Array.from(formData.entries())
            .filter(([key]) => key !== 'phone_alt')
            .map(([key, value]) => [key, value.toString()])
        ).toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string; data?: unknown } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormStatus('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '提交失败，请稍后再试';
        setFormError(serverMsg);
        setFormStatus('error');
      }
    } catch {
      setFormError('网络错误，请检查网络连接后重试');
      setFormStatus('error');
    }
  };

  return (
    <>
      <SEOHead
        title={pageTitle}
        description={pageDesc}
        keywords="开云体育关于我们,开云体育品牌,开云体育联系方式,开云体育信誉"
        canonicalUrl={pageUrl}
        ogTitle={pageTitle}
        ogDescription={pageDesc}
        ogUrl={pageUrl}
      />
      <SchemaOrg {...getWebPageSchema(pageTitle, pageDesc, pageUrl)} />
      <SchemaOrg {...getBreadcrumbSchema(breadcrumbSchemaItems)} />
      <SchemaOrg {...getOrganizationSchema()} />
      <SchemaOrg {...getFAQSchema(faqSchemaData)} />

      <Navbar />

      <main className="pt-16">
        {/* Page Header */}
        <section className="relative h-[200px] md:h-[260px] flex items-center">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Modern%20corporate%20office%20building%20with%20warm%20lighting%2C%20professional%20business%20atmosphere%2C%20clean%20architectural%20design%2C%20glass%20and%20steel%20structure%2C%20golden%20hour%20sunlight%2C%20minimalist%20composition%2C%20elegant%20professional%20style&width=1600&height=520&seq=about-hero-2026&orientation=landscape"
              alt="关于开云体育"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-black/45 to-black/25"></div>
          </div>
          <div className="relative z-10 max-w-7xl mx-auto w-full px-4 md:px-6">
            <Breadcrumb items={breadcrumbItems} />
            <h1 className="text-2xl md:text-3xl font-heading font-bold text-background-50 mt-3">关于我们</h1>
            <p className="text-sm text-background-50/80 mt-2">了解开云体育的品牌故事、核心价值与发展历程</p>
          </div>
        </section>

        {/* Brand Introduction */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex flex-col lg:flex-row items-center gap-10">
              <div className="flex-1">
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">品牌介绍</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  开云体育成立于{siteInfo.founded}年，是一家专注于体育竞技领域的综合性互联网平台。自创立以来，我们始终秉持<strong>诚信为本、技术驱动、用户至上</strong>的经营理念，致力于为广大体育爱好者提供安全、便捷、丰富的一站式体育娱乐服务。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed mb-4">
                  经过多年的深耕与发展，开云体育已积累了超过<strong>{siteInfo.stats[0].value}</strong>的忠实用户，日均赛事覆盖量达到<strong>{siteInfo.stats[1].value}</strong>场以上。平台汇聚了全球主流体育项目，涵盖足球、篮球、网球、电竞等<strong>{siteInfo.stats[2].value}</strong>余种赛事类型，满足不同体育爱好者的多样化需求。
                </p>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  开云体育拥有一支由资深技术专家和体育爱好者组成的核心团队。我们不断投入研发资源，采用国际先进的技术架构和安全体系，确保平台的稳定性、安全性和用户体验始终处于行业领先水平。
                </p>
              </div>
              <div className="flex-1">
                <div className="rounded-xl overflow-hidden">
                  <img
                    src="https://readdy.ai/api/search-image?query=Professional%20sports%20brand%20office%20team%20collaboration%20scene%2C%20warm%20modern%20workspace%2C%20creative%20atmosphere%2C%20diverse%20team%20members%20discussing%2C%20natural%20light%20from%20large%20windows%2C%20minimalist%20interior%20design%2C%20professional%20corporate%20photography&width=1200&height=800&seq=about-brand-2026&orientation=landscape"
                    alt="开云体育团队"
                    className="w-full h-auto"
                  />
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Stats */}
        <section className="py-10 bg-primary-500">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
              {siteInfo.stats.map((stat) => (
                <div key={stat.label} className="text-center">
                  <div className="text-3xl md:text-4xl font-heading font-bold text-background-50">{stat.value}</div>
                  <div className="text-sm text-background-50/80 mt-1">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Brand Values */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">品牌信誉</h2>
              <p className="text-sm text-foreground-500 mt-2">四大核心价值观，铸就值得信赖的体育平台</p>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {siteInfo.brandValues.map((value, index) => (
                <div key={value.title} className="p-6 rounded-xl bg-background-50 border border-background-200/70">
                  <div className="flex items-start gap-4">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-accent-100 text-accent-600">
                      <span className="font-heading font-bold text-sm">{index + 1}</span>
                    </div>
                    <div>
                      <h3 className="font-heading font-semibold text-foreground-900 text-sm mb-2">{value.title}</h3>
                      <p className="text-xs text-foreground-500 leading-relaxed">{value.description}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* FAQ Section */}
        <section className="py-14 md:py-16 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900">常见问题</h2>
              <p className="text-sm text-foreground-500 mt-2">用户最常咨询的问题及解答</p>
            </div>
            <div className="grid grid-cols-1 gap-3">
              {siteInfo.faqs.slice(0, 5).map((faq, index) => (
                <details key={index} className="group rounded-xl bg-background-100 border border-background-200/70 overflow-hidden">
                  <summary className="flex items-center justify-between p-5 cursor-pointer list-none">
                    <h4 className="font-heading font-medium text-foreground-900 text-sm pr-4">{faq.question}</h4>
                    <i className="ri-arrow-down-s-line text-foreground-400 group-open:rotate-180 transition-transform flex-shrink-0"></i>
                  </summary>
                  <div className="px-5 pb-5">
                    <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
                  </div>
                </details>
              ))}
            </div>
          </div>
        </section>

        {/* Contact Section */}
        <section className="py-14 md:py-16 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-10">
              {/* Contact Info */}
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">联系我们</h2>
                <p className="text-sm text-foreground-600 leading-relaxed mb-8">
                  如果您有任何疑问、建议或需要帮助，欢迎随时通过以下方式与我们联系。开云体育客服团队7x24小时为您提供专业、高效的服务。
                </p>
                <div className="flex flex-col gap-5">
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-phone-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-medium text-foreground-900 text-sm">客服热线</h4>
                      <p className="text-sm text-foreground-600 mt-0.5">{siteInfo.contact.phone}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-mail-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-medium text-foreground-900 text-sm">电子邮箱</h4>
                      <p className="text-sm text-foreground-600 mt-0.5">{siteInfo.contact.email}</p>
                    </div>
                  </div>
                  <div className="flex items-start gap-4 p-4 rounded-lg bg-background-50 border border-background-200/70">
                    <div className="w-10 h-10 flex items-center justify-center flex-shrink-0 rounded-lg bg-primary-100 text-primary-600">
                      <i className="ri-time-line text-lg"></i>
                    </div>
                    <div>
                      <h4 className="font-heading font-medium text-foreground-900 text-sm">工作时间</h4>
                      <p className="text-sm text-foreground-600 mt-0.5">{siteInfo.contact.workingHours}</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Contact Form */}
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-bold text-foreground-900 mb-5">发送消息</h2>
                <form
                  onSubmit={handleSubmit}
                  data-readdy-form
                  className="p-6 rounded-xl bg-background-50 border border-background-200/70"
                >
                  <div className="flex flex-col gap-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入您的邮箱地址"
                      />
                    </div>
                    <div>
                      <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                      <input
                        type="text"
                        id="subject"
                        name="subject"
                        required
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all"
                        placeholder="请输入消息主题"
                      />
                    </div>
                    <div>
                      <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">消息内容</label>
                      <textarea
                        id="message"
                        name="message"
                        required
                        maxLength={500}
                        rows={4}
                        className="w-full px-4 py-2.5 text-sm bg-background-100 border border-background-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent transition-all resize-none"
                        placeholder="请输入您的消息（500字以内）"
                      ></textarea>
                    </div>

                    {/* Honeypot */}
                    <div className="field-wrapper-company">
                      <input type="text" name="phone_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly />
                    </div>

                    <button
                      type="submit"
                      className="w-full py-3 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-medium rounded-lg transition-colors whitespace-nowrap cursor-pointer"
                    >
                      <i className="ri-send-plane-line mr-2"></i>发送消息
                    </button>
                  </div>

                  {formStatus === 'success' && (
                    <div className="mt-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                      <i className="ri-checkbox-circle-line mr-1.5"></i>消息已发送成功，我们会尽快回复您！
                    </div>
                  )}

                  {formStatus === 'error' && (
                    <div className="mt-4 p-3 rounded-lg bg-accent-100 text-accent-700 text-sm text-center">
                      <i className="ri-error-warning-line mr-1.5"></i>{formError}
                    </div>
                  )}
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
      <ScrollToTop />
    </>
  );
}